/**
 * Cliente do Worker `kyra-api` para o portal do aluno. Portado de
 * `frontend/lib/kyra-api.ts` (Next), adaptado pra SPA:
 *  - base URL vem de `env.apiUrl` (Vite) em vez de `process.env`
 *  - o token sai do supabase-js no browser (localStorage), não de cookie SSR
 *  - sem opção `cache` do Next; usamos fetch padrão (React Query cuida do cache)
 *
 * Fail-mode: lança `KyraApiError` com status + corpo, pra o caller distinguir
 * 401 (sessão morta → redireciona pro login) de 4xx/5xx genérico.
 */

import { env } from "./env";
import { getSupabase } from "./supabase";

export class KyraApiError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, body: string, message?: string) {
    super(message ?? `kyra-api ${status}`);
    this.status = status;
    this.body = body;
    this.name = "KyraApiError";
  }
}

interface FetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

function buildUrl(path: string): string {
  return `${env.apiUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

async function parseResponse<T>(res: Response, method: string, path: string): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    throw new KyraApiError(res.status, text, `${method} ${path} → ${res.status}`);
  }
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new KyraApiError(res.status, text, `invalid_json_from ${path}`);
  }
}

/**
 * Recupera o access_token atual da sessão Supabase. Lança 401 se não houver —
 * o caller normalmente redireciona pro /login.
 */
async function getAccessToken(): Promise<string> {
  const {
    data: { session },
  } = await getSupabase().auth.getSession();
  if (!session?.access_token) {
    throw new KyraApiError(401, "no_session", "Sessão expirada");
  }
  return session.access_token;
}

/**
 * Chamada autenticada (Bearer) ao Worker. Busca o token automaticamente
 * da sessão Supabase. Use em qualquer ponto autenticado do portal.
 *
 * @throws {KyraApiError} se status >= 400 ou sem sessão.
 */
export async function portalFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;
  const token = await getAccessToken();

  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(buildUrl(path), { method, headers, body: payload, signal });
  return parseResponse<T>(res, method, path);
}

/**
 * Fetch público (sem JWT) — pra endpoints `/public-portal/*` que alimentam
 * a tela pré-login (logo/nome do trainer). Único hoje:
 * `/public-portal/portal-info/:slug`.
 */
export async function publicPortalFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;

  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(buildUrl(path), { method, headers, body: payload, signal });
  return parseResponse<T>(res, method, path);
}
